// ============================================
// ROLC OneROLC - Main Application Entry
// Version: 3.6.0
// ============================================

import { CONFIG } from './config/constants.js';
import { setLastActivity } from './utils/storage.js';
import { initRouter, getCurrentRoute, subscribe } from './router/history.js';
import { navigateToLogin, navigateToHome } from './router/routes.js';
import { isAuthenticated } from './auth/authService.js';
import { renderLoginPage } from './pages/LoginPage.js';
import { renderHome } from './pages/HomePage.js';
import { renderSurveyPage } from './pages/SurveyPage.js';
import { renderAdminPage } from './pages/AdminPage.js';
import { showToast } from './components/Toast.js';
import { flushQueue, updateQueueBadge } from './services/queueService.js';

// ============================================
// Route Handler Map
// ============================================
const routeHandlers = {
    '/': renderHome,
    '/survey': renderSurveyPage,
    '/login': () => { renderLoginPage(); },
    '/login': () => { 
        if (!window.location.pathname.includes('login.html') && window.location.pathname !== '/login') {
            window.location.replace('/login.html');
        }
    },
    '/admin': async () => { 
        if (!isAuthenticated()) {
            window.location.replace('/login.html');
            return;
        }
        if (!window.location.pathname.includes('admin-panel.html') && window.location.pathname !== '/admin-panel') {
            window.location.replace('/admin-panel.html');
            return;
        }
        await renderAdminPage();
    },
    '/rolcadmin': async () => { 
        if (!isAuthenticated()) {
            window.location.replace('/login.html');
            return;
        }
        if (!window.location.pathname.includes('admin-panel.html') && window.location.pathname !== '/admin-panel') {
            window.location.replace('/admin-panel.html');
            return;
        }
        await renderAdminPage();
    }
};

function getPageContext() {
    const pathname = window.location.pathname || '';
    if (pathname.includes('login.html') || pathname === '/login') return 'login';
    if (pathname.includes('admin-panel.html') || pathname === '/admin-panel') return 'admin';
    return 'main';
}

// ============================================
// Initialize Application
// ============================================
export async function initApp() {
    console.log('[ROLC] Initializing app...');

    // Display version
    const versionEl = document.getElementById('versionDisplay');
    if (versionEl) {
        const isTest = window.location.search.includes('test=1') ||
                       new URLSearchParams(window.location.search).get('branch') === 'test';
        if (isTest) {
            versionEl.innerHTML = `<span style="background:#ef4444;color:#fff;padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:bold;">TEST BUILD</span> v${CONFIG.VERSION}`;
        } else {
            versionEl.textContent = `v${CONFIG.VERSION}`;
        }
    }

    // Setup activity tracking
    setupActivityTracking();

    // Initialize router
    initRouter();

    // Subscribe to route changes
    subscribe(handleRouteChange);

    // Check authentication and render initial route
    await handleInitialRoute();

    // Setup network handlers
    setupNetworkHandlers();

    // Update queue badge
    await updateQueueBadge();

    // Flush offline queue if online
    if (navigator.onLine) {
        await flushQueue();
    }

    console.log('[ROLC] App initialized');
}

// ============================================
// Activity Tracking
// ============================================
function setupActivityTracking() {
    const events = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    events.forEach(evt => {
        document.addEventListener(evt, setLastActivity, { passive: true });
    });
    setInterval(setLastActivity, 30000);
}

// ============================================
// Network Handlers
// ============================================
function setupNetworkHandlers() {
    window.addEventListener('online', async () => {
        showToast('Back online. Syncing...', 'info');
        await flushQueue();
        await updateQueueBadge();
    });

    window.addEventListener('offline', () => {
        showToast('You are offline. Responses will be saved locally.', 'warning');
    });
}

// ============================================
// Initial Route Handler
// ============================================
async function handleInitialRoute() {
    const context = getPageContext();
    
    if (context === 'login') {
        await renderLoginPage();
        return;
    }
    
    if (context === 'admin') {
        const isAdminNav = sessionStorage.getItem('rolc_admin_nav') === '1';
        if (isAuthenticated() && !isAdminNav) {
            window.location.replace('/login.html');
            return;
        }
        sessionStorage.removeItem('rolc_admin_nav');
        await renderAdminPage();
        return;
    }
    
    // Main index.html page - always start at login if token exists
    if (isAuthenticated() && !sessionStorage.getItem('rolc_just_logged_in')) {
        sessionStorage.removeItem('rolc_just_logged_in');
        window.location.replace('/login.html');
        return;
    }
    
    sessionStorage.removeItem('rolc_just_logged_in');
    
    await handleRouteChange();
}

// ============================================
// Route Change Handler
// ============================================
async function handleRouteChange() {
    const context = getPageContext();
    const { path, params } = getCurrentRoute();
    
    if (context === 'login') {
        await renderLoginPage();
        return;
    }
    
    if (context === 'admin') {
        if (!isAuthenticated()) {
            window.location.replace('/login.html');
            return;
        }
        await renderAdminPage();
        return;
    }
    
    // Main index.html page - use hash router
    updatePageTitle(path, params);

    const protectedRoutes = ['/', '/survey', '/admin', '/rolcadmin'];
    if (protectedRoutes.includes(path) && !isAuthenticated()) {
        await renderLoginPage(params);
        return;
    }

    const handler = routeHandlers[path] || routeHandlers['/'];
    
    try {
        await handler(params);
    } catch (err) {
        console.error('Route handler error:', err);
        showToast('Error loading page: ' + err.message, 'error');
    }

    setLastActivity();
}

// ============================================
// Page Title
// ============================================
function updatePageTitle(path, params) {
    const titles = {
        '/': 'ROLC OneROLC',
        '/survey': params.name ? `Survey: ${params.name}` : 'Survey',
        '/login': 'Login - ROLC OneROLC',
        '/admin': 'Admin Panel - ROLC OneROLC',
        '/rolcadmin': 'Admin Panel - ROLC OneROLC'
    };
    document.title = titles[path] || 'ROLC OneROLC';
}

// ============================================
// Bootstrap
// ============================================
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
} else {
    initApp();
}


